import { useState, useCallback, useEffect } from "react";
import { Link, useParams } from "wouter";
import { useBooking } from "@/hooks/use-booking-context";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { Breadcrumbs } from "@/components/layout/breadcrumbs";
import { StickyMobileBookCta } from "@/components/layout/sticky-mobile-book-cta";
import { FaqSection } from "@/components/seo/faq-section";
import { BookingWithOffers } from "@/components/promotions/booking-with-offers";
import { OtherHomeSwitch } from "@/components/property/other-home-switch";
import { TrustBadges } from "@/components/booking/trust-badges";
import { BookingOfferPopup } from "@/components/booking/booking-offer-popup";
import { WhyBookDirect } from "@/components/booking/why-book-direct";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Star, Images, ChevronLeft, ChevronRight, X, ArrowRight } from "lucide-react";
import { useSEO, useSEOSettings, generateProductSchema, generateBreadcrumbSchema, generateFAQSchema, generateKnowledgeGraphSchema, LOCATION_KEYWORDS, type SiteInfoSettings } from "@/lib/seo-utils";
import { formatTimeToStandard } from "@/lib/time-utils";
import type { PublicProperty, Photo } from "@shared/schema";
import { CANCELLATION_POLICY_SUMMARY, BALANCE_POLICY_SUMMARY, PROPERTY_MAX_GUESTS, BOOK_DIRECT_SAVINGS, STRIPE_SECURE_PAYMENT, SECURITY_DEPOSIT_SUMMARY, VERIFIED_STAY_LABEL } from "@shared/property-facts";
import { optimizedUrl, optimizedSrcSet } from "@/lib/img";
import { trackEvent } from "@/lib/meta-pixel";
import { KUALOA_PROPERTY_SLUG, KUALOA_STAY_PATH } from "@shared/property-context";

const buildBookingFAQs = (checkInTime?: string | null, checkOutTime?: string | null) => [
  {
    question: "How do I book direct?",
    answer: `Choose your check-in and check-out dates, select guests, and complete Stripe checkout. You receive instant email confirmation. ${BOOK_DIRECT_SAVINGS}`
  },
  {
    question: "Is payment secure?",
    answer: STRIPE_SECURE_PAYMENT
  },
  {
    question: "What is the cancellation policy?",
    answer: CANCELLATION_POLICY_SUMMARY
  },
  {
    question: "Is there a security deposit?",
    answer: SECURITY_DEPOSIT_SUMMARY
  },
  {
    question: "Can I pay in two payments?",
    answer: `Yes. For stays booked at least 60 days ahead you can pay 50% today and the remaining balance 30 days before check-in. We email you a secure payment link before the balance is due. ${BALANCE_POLICY_SUMMARY}`
  },
  {
    question: "What are check-in and check-out times?",
    answer: `Standard check-in is ${formatTimeToStandard(checkInTime) || "3:00 PM"} and check-out is ${formatTimeToStandard(checkOutTime) || "10:00 AM"} HST. Early check-in or late checkout may be available on request.`
  },
  {
    question: "Can I see full property details before booking?",
    answer: "Yes, visit our property details page for the full photo gallery, amenities list, map, guest reviews, and area guide."
  }
];

function PropertyStatsRow({
  property,
  reviewSummary,
  className,
}: {
  property?: PublicProperty;
  reviewSummary?: { averageRating: number; totalReviews: number };
  className?: string;
}) {
  const hasReviews = Boolean(reviewSummary && reviewSummary.totalReviews > 0);

  return (
    <div
      className={`grid items-start font-sans divide-x divide-sand-deep ${
        hasReviews ? "grid-cols-4" : "grid-cols-3"
      } ${className ?? ""}`}
    >
      <div className="text-center px-1 sm:px-3">
        <div className="text-luxury-xl sm:text-luxury-2xl font-serif font-bold text-ink">
          {property?.bedrooms || 3}
        </div>
        <div className="text-[10px] sm:text-[12px] uppercase tracking-[0.1em] font-semibold text-driftwood">
          Bedrooms
        </div>
      </div>
      <div className="text-center px-1 sm:px-3">
        <div className="text-luxury-xl sm:text-luxury-2xl font-serif font-bold text-ink">
          {property?.bathrooms || 2}
        </div>
        <div className="text-[10px] sm:text-[12px] uppercase tracking-[0.1em] font-semibold text-driftwood">
          Bathrooms
        </div>
      </div>
      <div className="text-center px-1 sm:px-3">
        <div className="text-luxury-xl sm:text-luxury-2xl font-serif font-bold text-ink">
          {property?.max_guests || PROPERTY_MAX_GUESTS}
        </div>
        <div className="text-[10px] sm:text-[12px] uppercase tracking-[0.1em] font-semibold text-driftwood">
          Guests
        </div>
      </div>
      {hasReviews && reviewSummary && (
        <div className="text-center px-1 sm:px-3">
          <div className="flex items-center justify-center text-luxury-xl sm:text-luxury-2xl font-serif font-bold text-ink">
            <Star className="h-4 w-4 sm:h-5 sm:w-5 text-sun fill-current mr-0.5 sm:mr-1" />
            {reviewSummary.averageRating.toFixed(1)}
          </div>
          <div className="text-[10px] sm:text-[12px] uppercase tracking-[0.1em] font-semibold text-driftwood">
            {reviewSummary.totalReviews} reviews
          </div>
        </div>
      )}
    </div>
  );
}

export default function BookingPage() {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const params = useParams<{ slug?: string }>();
  const { setProperty, propertySlug } = useBooking();
  const slug = params.slug || propertySlug || "";

  const { data: property, isLoading } = useQuery<PublicProperty>({
    queryKey: slug ? ["/api/properties", slug] : ["/api/property/public"],
  });
  const { data: staySettings } = useQuery<{
    minNights: number;
    minNightsSchedule: { nearMonths: number; nearMinNights: number; farMinNights: number } | null;
  }>({
    queryKey: ["/api/booking-settings", { ...(property?.slug ? { property: property.slug } : {}) }],
    enabled: Boolean(property?.id),
  });

  useEffect(() => {
    if (property?.id && property.slug) {
      setProperty(property.id, property.slug);
    }
  }, [property?.id, property?.slug, setProperty]);

  const { data: photos = [] } = useQuery<Photo[]>({
    queryKey: slug ? ["/api/photos", { property: slug }] : ["/api/photos"],
  });

  // Meta retargeting signal: /booking is the Meta ads landing page.
  useEffect(() => {
    if (!property?.title) return;
    trackEvent("ViewContent", {
      content_name: property.title,
      content_type: "product",
      content_category: "vacation_rental",
      // Same id the checkout and purchase events use, so Meta can follow one
      // home all the way down the funnel.
      content_ids: [property.slug || slug],
    });
  }, [property?.title, property?.slug, slug]);

  const openLightbox = useCallback((index: number) => {
    setCurrentImageIndex(index);
    setIsLightboxOpen(true);
  }, []);

  const nextImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev + 1) % photos.length);
  }, [photos.length]);

  const prevImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev - 1 + photos.length) % photos.length);
  }, [photos.length]);

  // Get actual review stats for dynamic display
  const { data: reviewSummary } = useQuery<{ averageRating: number; totalReviews: number }>({
    queryKey: slug ? ["/api/reviews/summary", { property: slug }] : ["/api/reviews/summary"],
  });
  // Every link back to "the property" goes to this home's own page.
  const detailsHref = slug && slug !== KUALOA_PROPERTY_SLUG ? `/properties/${slug}` : KUALOA_STAY_PATH;
  const isKualoa = !slug || slug === KUALOA_PROPERTY_SLUG;

  // Get featured reviews for social proof
  const { data: featuredReviews = [] } = useQuery<any[]>({
    queryKey: ["/api/reviews/featured"],
  });

  // The nightly rate for structured data comes from the property record.
  //
  // This used to quote a seven-night stay starting tomorrow, purely to read a
  // rate off it. That is a network request on every page load for a number the
  // property already carries, and it quoted an arbitrary stay whose shape could
  // change what came back: a check-in tomorrow always falls inside the
  // last-minute window, which suppresses the long-stay discount, so the answer
  // happened to be right for reasons unrelated to what was being asked.
  //
  // `base_rate` is resolved server-side against today's seasonal rules, needs no
  // request, and is what the home page already publishes - so the two pages can
  // no longer disagree about the property's price.

  // Get SEO settings for schema generation
  const { seoSettings } = useSEOSettings();

  // Get site settings for dynamic schema content
  const { data: siteSettings } = useQuery<SiteInfoSettings>({
    queryKey: ['/api/settings/site_info'],
    retry: false,
  });

  // Build booking FAQs with property-driven check-in/check-out times
  const bookingFAQs = buildBookingFAQs(property?.check_in_time, property?.check_out_time);

  // SEO implementation for booking page with FAQ schema for rich snippets
  const seoHead = useSEO({
    pageType: "booking",
    title: slug && property
      ? `Book ${property.title} | Vacation Rental Oahu`
      : 'Book Oahu Beachfront Rental | The Kualoa Beach House',
    description: slug && property
      ? `Check dates and book ${property.title} direct. Oceanfront in Ka'a'awa, up to ${property.max_guests} guests, no platform fees, full refund up to 30 days before check-in.`
      : undefined,
    keywords: `${LOCATION_KEYWORDS.bookingPrimary}, oahu beach house reservation, book beachfront oahu direct, no fee oahu rental booking`,
    ogType: "product",
    canonical: slug
      ? `https://vacationrentaloahu.co/properties/${slug}/book`
      : "https://vacationrentaloahu.co/booking",
    twitterCard: "summary_large_image",
    structuredData: seoSettings && property ? [
      generateKnowledgeGraphSchema({ property, seoSettings, siteInfoSettings: siteSettings }),
      generateProductSchema(property, undefined, seoSettings),
      generateBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Our Homes", url: "/properties" },
        { name: "Book Your Stay", url: slug ? `/properties/${slug}/book` : "/booking" }
      ]),
      generateFAQSchema(bookingFAQs)
    ] : []
  });

  if (isLoading) {
    return (
      <>
      {seoHead}
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-28 pb-16">
          <div className="container mx-auto px-4">
            <Skeleton className="h-8 w-1/3 mb-8" />
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <Skeleton className="h-64 w-full" />
              </div>
              <div>
                <Skeleton className="h-96 w-full" />
              </div>
            </div>
          </div>
        </div>
      </div>
      </>
    );
  }

  // Get hero photo
  const heroPhoto = photos.find((p: Photo) => p.is_featured) || photos[0];

  return (
    <>
    {seoHead}
    <div className="min-h-screen bg-shell pb-24 lg:pb-0">
      <Header />
      
      <main id="main-content" className="pt-20">
        <div className="container mx-auto px-4 pt-4">
          <Breadcrumbs items={[{ name: "Home", url: "/" }, ...(slug ? [{ name: "Our Homes", url: "/properties" }] : []), { name: "Book", url: slug ? `/properties/${slug}/book` : "/booking" }]} />
        </div>
        {/* Hero Section with Property Image */}
        <section className="relative bg-shell">
          <div className="container mx-auto px-4 py-8 md:py-12">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Left: Property Info */}
              <div className="order-2 lg:order-1">
                <p className="text-[11px] md:text-xs font-sans font-semibold uppercase tracking-[0.3em] text-driftwood mb-3">
                  {isKualoa ? "Oceanfront Beach House" : property?.title || "Oceanfront home"}
                </p>
                <h1 className="text-luxury-3xl md:text-luxury-4xl font-serif font-normal text-forest mb-4 leading-tight tracking-luxury-tight" data-testid="booking-page-title">
                  Check Availability & Book Direct
                </h1>
                <p className="text-luxury-lg text-ink-soft mb-4 leading-relaxed font-sans">
                  Reserve {isKualoa ? "our Ka'a'awa beachfront vacation rental" : property?.title || "this home"} with secure direct checkout: instant confirmation, transparent pricing, no platform fees.
                </p>
                <p className="text-luxury-sm text-ink-soft mb-6 font-sans">
                  <Link href={detailsHref} className="text-ink hover:text-palm underline decoration-sand-deep transition-colors inline-flex items-center gap-1">
                    View full property details, photos & amenities <ArrowRight className="h-3 w-3" />
                  </Link>
                </p>

                {/* Mobile only: the hero photo is hidden below lg so the date picker lands
                    above the fold for ad traffic. The gallery stays one tap away here. */}
                {photos.length > 0 && (
                  <button
                    type="button"
                    onClick={() => openLightbox(0)}
                    className="lg:hidden mb-2 inline-flex min-h-[44px] items-center gap-2 text-luxury-sm font-sans font-semibold text-ink underline decoration-sand-deep"
                    data-testid="mobile-view-photos"
                  >
                    <Images className="h-4 w-4" />
                    View all {photos.length} photos
                  </button>
                )}

                {/* Property Stats — desktop only; on mobile these render below the booking form */}
                <PropertyStatsRow
                  property={property}
                  reviewSummary={reviewSummary}
                  className="hidden lg:grid mb-8"
                />

                {/* Trust Badges — desktop only; on mobile these render below the booking form */}
                <TrustBadges className="hidden lg:grid" />
              </div>

              {/* Right: Hero Image — desktop only. On mobile it pushed the booking form a
                  full screen below the fold; the ad creative already showed the house. */}
              <div className="order-1 lg:order-2 hidden lg:block">
                {heroPhoto ? (
                  <div className="relative rounded-2xl overflow-hidden aspect-[16/9] lg:aspect-[4/3] group cursor-pointer border border-sand-deep" onClick={() => openLightbox(0)}>
                    <img 
                      src={optimizedUrl(heroPhoto.url, 960)}
                      srcSet={optimizedSrcSet(heroPhoto.url, [480, 768, 960, 1200])}
                      sizes="(max-width: 1024px) 100vw, 50vw"
                      alt={heroPhoto.alt || property?.title || "Beach house"} 
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      data-testid="booking-hero-image"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
                    
                    {/* View All Photos Button */}
                    {photos.length > 0 && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          openLightbox(0);
                        }}
                        variant="secondary"
                        size="sm"
                        className="absolute bottom-4 right-4 bg-shell/95 hover:bg-shell text-ink font-sans font-semibold text-luxury-sm border border-sand-deep backdrop-blur-sm"
                        data-testid="view-all-photos-button"
                      >
                        <Images className="h-4 w-4 mr-2 text-ink" />
                        View all {photos.length} photos
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="rounded-2xl overflow-hidden aspect-[16/9] lg:aspect-[4/3] bg-sand flex items-center justify-center border border-sand-deep">
                    <span className="text-[12px] uppercase tracking-[0.3em] font-semibold text-driftwood">Property Photo</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Main Booking Section */}
        <section className="py-8 md:py-16 bg-shell border-t border-sand-deep">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
              {/* Booking Widget — first in DOM so mobile tab/screen-reader order matches visual order; lg:order-2 keeps it on the right on desktop */}
              <div className="lg:order-2 lg:col-span-1">
                <div className="sticky top-24" id="booking-form">
                  <div className="relative">
                    {/* Decorative border removed in favor of quiet border inside widget */}
                    <div className="relative">
                      {staySettings && staySettings.minNights > 1 && (
                        <p className="text-[11px] uppercase tracking-[0.15em] text-driftwood font-semibold font-sans text-center mb-3">
                          {staySettings.minNightsSchedule
                            ? `${staySettings.minNightsSchedule.nearMinNights}-night minimum for check-ins within ${staySettings.minNightsSchedule.nearMonths} ${staySettings.minNightsSchedule.nearMonths === 1 ? "month" : "months"} · ${staySettings.minNightsSchedule.farMinNights} nights afterward`
                            : `${staySettings.minNights}-night minimum stay`}
                        </p>
                      )}
                      <BookingWithOffers propertyId={property?.id} propertySlug={property?.slug ?? undefined} />
                      <OtherHomeSwitch currentSlug={property?.slug || slug || KUALOA_PROPERTY_SLUG} className="mt-6" />
                    </div>
                  </div>

                  {/* Mobile-only: property stats + trust badges relocated from the hero */}
                  <div className="lg:hidden mt-8 space-y-6">
                    <PropertyStatsRow property={property} reviewSummary={reviewSummary} />
                    <TrustBadges />
                  </div>
                </div>
              </div>
              {/* Left: Content (renders after the booking form on mobile) */}
              <div className="lg:order-1 lg:col-span-2 space-y-10">
                
                {/* Featured Review Quote */}
                {featuredReviews.length > 0 && featuredReviews[0]?.comment && (
                  <div className="relative bg-shell rounded-2xl p-8 border border-sand-deep">
                    <div className="absolute top-4 left-6 text-6xl text-sand-deep font-serif leading-none">"</div>
                    <blockquote className="relative z-10 pt-6">
                      <p className="text-luxury-lg text-ink font-serif italic leading-relaxed mb-4">
                        {featuredReviews[0].comment.length > 200 
                          ? featuredReviews[0].comment.substring(0, 200) + "..." 
                          : featuredReviews[0].comment}
                      </p>
                      <footer className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-sand border border-sand-deep flex items-center justify-center">
                          <span className="text-ink font-serif font-bold">
                            {featuredReviews[0].guest_name?.charAt(0) || 'G'}
                          </span>
                        </div>
                        <div>
                          <cite className="not-italic font-semibold font-sans text-ink">
                            {featuredReviews[0].guest_name || 'Guest'}
                          </cite>
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1">
                              {[...Array(5)].map((_, i) => (
                                <Star 
                                  key={i} 
                                  className={`h-3 w-3 ${i < (featuredReviews[0].rating || 5) ? 'text-sun fill-current' : 'text-sand-deep'}`} 
                                />
                              ))}
                            </div>
                            <span className="text-[11px] font-sans font-semibold uppercase tracking-[0.12em] text-palm">
                              {VERIFIED_STAY_LABEL}
                            </span>
                          </div>
                        </div>
                      </footer>
                    </blockquote>
                  </div>
                )}

                {/* Property details — link to /stay (avoid duplicate content) */}
                <Card className="border-sand-deep bg-shell rounded-2xl">
                  <CardContent className="p-6">
                    <h2 className="text-luxury-xl font-serif font-normal text-forest mb-2 tracking-luxury-tight">
                      {property?.title || "Oceanfront Beach House"} in Ka'a'awa
                    </h2>
                    <p className="text-ink-soft font-sans text-luxury-sm mb-4 leading-relaxed">
                      {isKualoa
                        ? `3-bedroom beachfront home · Private beach access · Near Kualoa Ranch · Sleeps ${property?.max_guests || 6}`
                        : `${property?.bedrooms ?? ""}-bedroom oceanfront home · ${property?.bathrooms ?? ""} bathrooms · Near Kualoa Ranch · Sleeps ${property?.max_guests ?? ""}`}
                    </p>
                    <Link href={detailsHref}>
                      <Button variant="outline" size="sm" className="font-sans font-semibold border-sand-deep text-ink hover:bg-sand whitespace-normal h-auto min-h-9 text-left">
                        Full gallery, amenities & reviews <ArrowRight className="h-3 w-3 ml-2" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>

                {/* House Rules */}
                <div>
                  <p className="text-[11px] md:text-xs font-sans font-semibold uppercase tracking-[0.3em] text-driftwood mb-3">
                    House Rules
                  </p>
                  <h2 className="text-luxury-2xl font-serif font-normal text-forest mb-6 tracking-luxury-tight">
                    Good to know
                  </h2>
                  <div className="grid grid-cols-3 gap-3 sm:gap-6">
                    <Card className="border-sand-deep bg-shell rounded-2xl">
                      <CardContent className="p-4 sm:p-6 text-center">
                        <div className="text-luxury-lg sm:text-luxury-xl md:text-luxury-2xl font-serif font-bold text-ink mb-1">
                          {property?.check_in_time ? formatTimeToStandard(property.check_in_time) : '3:00 PM'}
                        </div>
                        <div className="text-[11px] uppercase tracking-[0.15em] font-sans font-semibold text-driftwood">Check-in</div>
                      </CardContent>
                    </Card>
                    <Card className="border-sand-deep bg-shell rounded-2xl">
                      <CardContent className="p-4 sm:p-6 text-center">
                        <div className="text-luxury-lg sm:text-luxury-xl md:text-luxury-2xl font-serif font-bold text-ink mb-1">
                          {property?.check_out_time ? formatTimeToStandard(property.check_out_time) : '10:00 AM'}
                        </div>
                        <div className="text-[11px] uppercase tracking-[0.15em] font-sans font-semibold text-driftwood">Check-out</div>
                      </CardContent>
                    </Card>
                    <Card className="border-sand-deep bg-shell rounded-2xl">
                      <CardContent className="p-4 sm:p-6 text-center">
                        <div className="text-luxury-lg sm:text-luxury-xl md:text-luxury-2xl font-serif font-bold text-ink mb-1">
                          {property?.max_guests || PROPERTY_MAX_GUESTS}
                        </div>
                        <div className="text-[11px] uppercase tracking-[0.15em] font-sans font-semibold text-driftwood">Max guests</div>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                <WhyBookDirect />

                <div className="border-t border-sand-deep pt-10">
                  <p className="text-[11px] md:text-xs font-sans font-semibold uppercase tracking-[0.3em] text-driftwood mb-3">
                    Support
                  </p>
                  <FaqSection title="Booking questions" items={bookingFAQs} />
                </div>
              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
      <StickyMobileBookCta />
      <BookingOfferPopup />

      {/* Photo Lightbox */}
      <Dialog open={isLightboxOpen} onOpenChange={setIsLightboxOpen}>
        <DialogContent 
          className="max-w-5xl w-full h-full max-h-[90vh] p-0 bg-[hsl(36_20%_8%)] border-ink/30 [&>button:last-child]:hidden"
          aria-labelledby="lightbox-title"
        >
          <DialogTitle id="lightbox-title" className="sr-only">
            Property Photo Gallery - Image {currentImageIndex + 1} of {photos.length}
          </DialogTitle>
          <div className="relative w-full h-full flex items-center justify-center px-16">
            {/* Close button */}
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-4 right-4 z-10 text-white hover:bg-white/20"
              onClick={() => setIsLightboxOpen(false)}
              aria-label="Close photo gallery"
            >
              <X className="h-6 w-6" />
            </Button>

            {/* Navigation buttons */}
            {photos.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-20 text-ink hover:text-ink bg-shell"
                  onClick={prevImage}
                  aria-label="Previous image"
                >
                  <ChevronLeft className="h-8 w-8" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-20 text-ink hover:text-ink bg-shell"
                  onClick={nextImage}
                  aria-label="Next image"
                >
                  <ChevronRight className="h-8 w-8" />
                </Button>
              </>
            )}

            {/* Current image */}
            {photos[currentImageIndex] && (
              <img
                src={optimizedUrl(photos[currentImageIndex].url, 1600)}
                srcSet={optimizedSrcSet(photos[currentImageIndex].url, [768, 1200, 1600])}
                sizes="100vw"
                alt={photos[currentImageIndex].alt || `Property photo ${currentImageIndex + 1}`}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
            )}

            {/* Image counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-luxury-sm font-serif">
              {currentImageIndex + 1} / {photos.length}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </>
  );
}
